import React, { useState, useRef, useEffect } from 'react';
import ActionButtons from './ActionButtons';
import { useTranslation } from 'react-i18next';
import { GENERAL_ERROR_UPDATE_PAGE_MESSAGE } from '../../../../../i18n/keyConstants';

const GenericInputContainer = ({
    isEditing,
    setIsEditing,
    isHovered,
    isFieldNull,
    handleSave,
    handleCancel,
    children,
    valueHasntChanged,
    cancelMessage,
    saveOnEnter = true,
}) => {
    const [DisplayComponent, EditComponent] = children;

    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const ref = useRef(null);
    const { t } = useTranslation(['errorMessages']);


    useEffect(() => {
        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddValueClick = () => {
        setIsEditing(true);
    };

    const handleEditValueClick = () => {
        setIsEditing(true);
    };

    const onSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        if (valueHasntChanged()) {
            await new Promise((r) => setTimeout(r, 500));
            setIsEditing(false);
            setIsSaving(false);
            setErrorMessage('');
            return { success: true };
        }
        const response = await handleSave();
        setIsSaving(false);
        if (response.success) {
            setIsEditing(false);
            setErrorMessage('');
        } else {
            // Set error message as the first nonempty of below
            [response.error, response.message, t(GENERAL_ERROR_UPDATE_PAGE_MESSAGE)].some((m) => {
                if (m && m.length) {
                    setErrorMessage(m);
                    return true;
                }
                return false;
            });
        }
    };

    const onCancel = () => {
        if (cancelMessage) {
            var shouldCancel = window.confirm('Vill du verkligen avbryta?');
            if (!shouldCancel) return;
        }

        setIsSaving(false);
        setIsEditing(false);
        handleCancel();
        setErrorMessage('');
    };

    const handleClick = e => {
        if (ref?.current?.contains(e.target)) {
            return;
        }
        onBlur();
};

    const onKeyDown = (event) => {
        // Enter selects active index and creates tag if shift is not pressed
        if (event.keyCode === 13 && !event.shiftKey && saveOnEnter) {
            onSave();
            event.preventDefault();
        }

        // Escape cancels
        else if (event.keyCode === 27) {
            onCancel();
            event.preventDefault();
        }
    };

    const onKeyDownButton = (event) => {
        if (event.keyCode === 13 && !isEditing) {
            handleEditValueClick();
        }
    }

    const onFocus = () => setIsFocused(true);

    const onBlur = () => setIsFocused(false);
    
    if (isFieldNull && !isEditing) {
        return (
            <div
                className='add-button-container'
                onClick={handleAddValueClick}
                onKeyDown={onKeyDownButton}
                tabIndex={0}
                onFocus={onFocus}
                onBlur={onBlur}
                ref={ref}>
                <button className={'btn btn-light add-button' + (isHovered || isFocused ? '' : ' d-none')} tabIndex={-1}>
                    <div className='oi oi-plus text-primary' />
                </button>
            </div>
        );
    }
    
    if (isEditing) {
        return (
            <div className='w-100 h-100'>
                {React.cloneElement(EditComponent, { onKeyDown, onSave, isInvalid: errorMessage !== '', autoFocus: true })}
                {errorMessage && <div className='crm-invalid-msg'>{errorMessage}</div>}
                <ActionButtons {...{ onCancel, onSave, isSaving }} />
            </div>
        );
    }

    return (
        <div className='d-flex justify-content-between align-content-bottom h-100 display-component-wrapper'>
            {DisplayComponent}
            <div
                onKeyDown={onKeyDownButton}
                tabIndex={0}
                onFocus={onFocus}
                onBlur={onBlur}
                ref={ref}>
                <button className={'btn btn-light edit-button' + (isHovered || isFocused ? '' : ' d-none')} onClick={handleEditValueClick} tabIndex={-1}>
                <div className='oi oi-pencil' />
                </button>
                </div>
        </div>
    );
};

export default GenericInputContainer;
